import { HttpErrorResponse } from '@angular/common/http';
import { createHttpActions } from '@next/arable-lib/shared';
import { createAction, createActionGroup, props } from '@ngrx/store';

import {
  GeneralQuestions,
  GeneralQuestionsRequest,
  ImageWithLegendResponse,
  SoilQuestions,
  SoilQuestionsRequest,
  TgaElevationImageParams,
  TgaSoilImageParams,
  TgaBiomassImageParams,
  TgaBiomassPotatoImageParams,
  DisplayAdvice,
  VarietyPropertiesTGADto,
  GenericGeneralQuestionsRequest,
  TgaCropFieldWithStatus,
  TgaSteps,
  GenericSoilQuestionsRequest,
  PlantingQuestions,
  PlantingQuestionsRequest,
  GenericPlantingQuestionsRequest,
  TranslationParams,
  ImageWithLegendAndDescriptionResponse,
  GenericPlantingQuestionsWithStatus,
  GeneralGenericQuestionsWithStatus,
  GenericSoilQuestionsWithStatus,
  NotificationDto,
  WaterAuthorityExceedanceResultDto,
  TgaSatelliteImagesParams,
  SatelliteImage,
  FieldObservationImage,
  FieldObservationImagesParams,
} from '../shared/models/models';

export const [fetchGeneralQuestions, fetchGeneralQuestionsSuccess, fetchGeneralQuestionsFailure] = createHttpActions<
  { cropFieldId: number; cropYear: number },
  { questions: GeneralQuestions },
  { error: string }
>('Fetch TGA General Questions');

export const [postGeneralQuestions, postGeneralQuestionsSuccess, postGeneralQuestionsFailure] = createHttpActions<
  { answers: GeneralQuestionsRequest },
  void,
  { error: HttpErrorResponse }
>('Post TGA General Questions');

export const [fetchSoilQuestions, fetchSoilQuestionsSuccess, fetchSoilQuestionsFailure] = createHttpActions<
  { cropFieldId: number; cropYear: number },
  { questions: SoilQuestions },
  { error: unknown }
>('Fetch TGA Soil Questions');

export const [postSoilQuestions, postSoilQuestionsSuccess, postSoilQuestionsFailure] = createHttpActions<
  { answers: SoilQuestionsRequest },
  void,
  { error: HttpErrorResponse }
>('Post TGA Soil Questions');

export const [fetchGenericGeneralQuestions, fetchGenericGeneralQuestionsSuccess, fetchGenericGeneralQuestionsFailure] = createHttpActions<
  { cropYear: number },
  { questions: GeneralGenericQuestionsWithStatus },
  { error: string }
>('Fetch TGA Generic General Questions');

export const [postGenericGeneralQuestions, postGenericGeneralQuestionsSuccess, postGenericGeneralQuestionsFailure] = createHttpActions<
  { answers: GenericGeneralQuestionsRequest },
  void,
  { error: HttpErrorResponse }
>('Post TGA Generic General Questions');

export const [loadCropFields, loadCropFieldsSuccess, loadCropFieldsFailure] = createHttpActions<
  { cropYear: number },
  { cropFields: TgaCropFieldWithStatus[] },
  { error: string }
>('Load TGA Crop Fields');

export const [fetchGeneralAdvice, fetchGeneralAdviceSuccess, fetchGeneralAdviceFailure] = createHttpActions<
  { cropFieldId: number; cropYear: number },
  { generalAdvice: DisplayAdvice[]; cropFieldId: number; cropYear: number; translationParams: TranslationParams },
  { error: string }
>('Fetch TGA General Advice');

export const [fetchSoilAdvice, fetchSoilAdviceSuccess, fetchSoilAdviceFailure] = createHttpActions<
  { cropFieldId: number; cropYear: number },
  { soilAdvice: DisplayAdvice[]; cropFieldId: number; cropYear: number; translationParams: TranslationParams },
  { error: string }
>('Fetch TGA Soil Advice');
export const [fetchPlantingAdvice, fetchPlantingAdviceSuccess, fetchPlantingAdviceFailure] = createHttpActions<
  { cropFieldId: number; cropYear: number },
  { plantingAdvice: DisplayAdvice[]; cropFieldId: number; cropYear: number; translationParams: TranslationParams },
  { error: string }
>('Fetch TGA Planting Advice');
export const [fetchCultivationAdvice, fetchCultivationAdviceSuccess, fetchCultivationAdviceFailure] = createHttpActions<
  { cropFieldId: number; cropYear: number },
  { cultivationAdvice: DisplayAdvice[]; cropFieldId: number; cropYear: number; translationParams: TranslationParams },
  { error: string }
>('Fetch TGA Cultivation Advice');

export const StepperActions = createActionGroup({
  source: 'TGA Stepper',
  events: {
    'Update Active Step': props<{ stepName: TgaSteps }>(),
    'Set Active Step By Key': props<{ stepKey: TgaSteps }>(),
    'Move To Next Step': props<{ nextStep: TgaSteps | null }>(),
  },
});

export const QuestionnaireActions = createActionGroup({
  source: 'TGA Questionnaire',
  events: {
    'Set Specific Questionnaire Context': props<{ cropFieldId: number; cropYear: number }>(),
  },
});

export const [loadTgaSoilImage, loadTgaSoilImageSuccess, loadTgaSoilImageFailure] = createHttpActions<
  TgaSoilImageParams,
  ImageWithLegendResponse,
  { error: string }
>('Load TGA Soil Image');

export const [loadTgaBiomassImage, loadTgaBiomassImageSuccess, loadTgaBiomassImageFailure] = createHttpActions<
  TgaBiomassImageParams,
  ImageWithLegendResponse,
  { error: string }
>('Load TGA Biomass Image');

export const [loadTgaBiomassPotatoImage, loadTgaBiomassPotatoImageSuccess, loadTgaBiomassPotatoImageFailure] =
  createHttpActions<TgaBiomassPotatoImageParams, ImageWithLegendAndDescriptionResponse, { error: string }>(
    'Load TGA Biomass Potato Image',
  );

export const [fetchVarietyPropertiesByEdiCode, fetchVarietyPropertiesByEdiCodeSuccess, fetchVarietyPropertiesByEdiCodeFailure] =
  createHttpActions<
    { ediCode: number },
    VarietyPropertiesTGADto,
    { error: string }
  >('Fetch Variety Properties By EdiCode');

export const [fetchGenericSoilQuestions, fetchGenericSoilQuestionsSuccess, fetchGenericSoilQuestionsFailure] = createHttpActions<
  { cropYear: number },
  { questions: GenericSoilQuestionsWithStatus },
  { error: unknown }
>('Fetch TGA Generic Soil Questions');

export const [postGenericSoilQuestions, postGenericSoilQuestionsSuccess, postGenericSoilQuestionsFailure] = createHttpActions<
  { answers: GenericSoilQuestionsRequest },
  void,
  { error: HttpErrorResponse }
>('Post TGA Generic Soil Questions');

export const [fetchPlantingQuestions, fetchPlantingQuestionsSuccess, fetchPlantingQuestionsFailure] = createHttpActions<
  { cropFieldId: number; cropYear: number },
  { questions: PlantingQuestions },
  { error: unknown }
>('Fetch TGA Planting Questions');

export const [postPlantingQuestions, postPlantingQuestionsSuccess, postPlantingQuestionsFailure] = createHttpActions<
  { answers: PlantingQuestionsRequest },
  void,
  { error: HttpErrorResponse }
>('Post TGA Planting Questions');

export const [fetchGenericPlantingQuestions, fetchGenericPlantingQuestionsSuccess, fetchGenericPlantingQuestionsFailure] = createHttpActions<
  { cropYear: number },
  { questions: GenericPlantingQuestionsWithStatus },
  { error: unknown }
>('Fetch TGA Generic Planting Questions');

export const [postGenericPlantingQuestions, postGenericPlantingQuestionsSuccess, postGenericPlantingQuestionsFailure] = createHttpActions<
  { answers: GenericPlantingQuestionsRequest },
  void,
  { error: HttpErrorResponse }
>('Post TGA Generic Planting Questions');

export const [loadTgaHeightImage, loadTgaHeightImageSuccess, loadTgaHeightImageFailure] = createHttpActions<
  TgaElevationImageParams,
  ImageWithLegendAndDescriptionResponse,
  { error: string }
>('Load TGA Height Image');

export const [loadSatelliteImages, loadSatelliteImagesSuccess, loadSatelliteImagesFailure] = createHttpActions<
  TgaSatelliteImagesParams,
  SatelliteImage[],
  { error: string }
>('Load TGA Satellite Images');

export const [loadFieldObservationImages, loadFieldObservationImagesSuccess, loadFieldObservationImagesFailure] = createHttpActions<
  FieldObservationImagesParams,
  FieldObservationImage[],
  { error: string }
>('Load TGA Field Observation Images');

export const cancelCultivationRequests = createAction('Cancel TGA Cultivation Requests');

export const [loadWaterAuthorityExceedances, loadWaterAuthorityExceedancesSuccess, loadWaterAuthorityExceedancesFailure] =
  createHttpActions<number[], { results: WaterAuthorityExceedanceResultDto[] }, { error: string }>('Load Water Authority Exceedances');

export const [loadNotifications, loadNotificationsSuccess, loadNotificationsFailure] = createHttpActions<
  { cropYear: number },
  { notifications: NotificationDto[] },
  { error: string }
>('Load TGA Notifications');

export const [closeNotification, closeNotificationSuccess, closeNotificationFailure] = createHttpActions<
  { notificationId: number },
  void,
  { error: HttpErrorResponse }
>('Close TGA Notification');
